import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { CSSProperties } from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps, CssThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { FormControlLabel, Radio as MUIRadio } from '@mui/material';
import { FieldSubLabel } from '@styles/forms';
import { getTranslation, getLabelText } from '@util/common';
import { Localisation } from '@model/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface RadioProps {
  theme?: Theme;
  testId?: string;
  label?: string | Localisation;
  subLabel?: string;
  value: string;
  style?: CSSProperties;
  checked?: boolean;
  onChange?: (event: any) => void;
  renderLabel?: () => JSX.Element;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container: any = styled.div(({ theme }: CssThemeProps) => ({
  ['.MuiSvgIcon-root']: {
    width: 28,
    height: 28,

    ['&:last-of-type']: {
      width: 20,
      height: 20,
      marginLeft: 4,
      marginTop: 4
    }
  },

  ['.MuiRadio-colorPrimary']: {
    color: theme.custom.colors.group10.light
  },

  ['.MuiRadio-colorPrimary.Mui-checked']: {
    color: theme.custom.states.selected.dark
  },

  ['.MuiFormControlLabel-root']: {
    marginLeft: 0,
    marginRight: theme.custom.spacing.large
  },

  ['.MuiButtonBase-root']: {
    padding: theme.custom.spacing.xSmall
  },

  ['.MuiIconButton-root']: {
    marginLeft: -theme.custom.spacing.xSmall
  },

  ['.MuiIconButton-label > div']: {
    padding: 0
  }
}));

export const RadioLabel = styled.label(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  pointerEvents: 'none'
}));

/* ******************** *
 *    RadioComponent    *
 * ******************** */

export const RadioComponent = (props: RadioProps) => {
  const { label, value, subLabel, style, checked, onChange, renderLabel } = props;
  const t: any = useI18NextContext();

  return (
    <Container data-testid={props.testId || TestId.radio.main} style={style}>
      <FormControlLabel
        data-testid={TestId.radio.formControl}
        value={value}
        checked={checked}
        onChange={onChange}
        control={<MUIRadio color="primary" />}
        label={
          renderLabel ? (
            renderLabel()
          ) : (
            <RadioLabel data-testid={TestId.radio.label}>
              {label && getLabelText(label, t)}
              {subLabel && (
                <FieldSubLabel data-testid={TestId.radio.subLabel}>{getTranslation(t, subLabel)}</FieldSubLabel>
              )}
            </RadioLabel>
          )
        }
        labelPlacement="end"
      />
    </Container>
  );
};

export const Radio = withTheme(RadioComponent);
